/**
 * Heuristic to group ANC encounters into "the same pregnancy" without a
 * formal Pregnancy entity in the data model.
 *
 * Sort encounters by visit date desc, walk back from the current encounter,
 * and include each prior encounter while the gap to the next (newer) one is
 * at most MAX_INTRA_PREGNANCY_GAP_DAYS. The first gap larger than that marks
 * the boundary with the previous pregnancy.
 */

import { differenceInCalendarDays } from "date-fns";

export const MAX_INTRA_PREGNANCY_GAP_DAYS = 300;

type EncounterLike = {
  id: string;
  createdAt: string;
};

function diffDays(a: Date, b: Date): number {
  return Math.abs(differenceInCalendarDays(a, b));
}

/**
 * Returns the subset of `encounters` that belong to the same pregnancy as
 * the anchor encounter, ordered most-recent first.
 *
 * - If `anchorEncounterId` is provided, that encounter is used as the anchor
 *   and is excluded from the result (caller's "current" / in-progress visit).
 *   Returns an empty array if the anchor isn't in `encounters`.
 * - If `anchorEncounterId` is omitted, the most-recent encounter is used as
 *   the anchor and is included in the result. Useful for showing all visits
 *   in the current pregnancy outside the ANC route, where there's no
 *   in-progress encounter to exclude.
 */
export function selectCurrentPregnancyEncounters<E extends EncounterLike>(
  encounters: readonly E[],
  anchorEncounterId?: string,
): E[] {
  if (encounters.length === 0) return [];

  const sorted = [...encounters].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  let anchorIndex: number;
  let result: E[];
  if (anchorEncounterId != null) {
    anchorIndex = sorted.findIndex((e) => e.id === anchorEncounterId);
    if (anchorIndex === -1) return [];
    result = [];
  } else {
    anchorIndex = 0;
    result = [sorted[0]];
  }

  let referenceDate = new Date(sorted[anchorIndex].createdAt);

  for (let i = anchorIndex + 1; i < sorted.length; i++) {
    const candidate = sorted[i];
    const candidateDate = new Date(candidate.createdAt);
    if (diffDays(referenceDate, candidateDate) > MAX_INTRA_PREGNANCY_GAP_DAYS) {
      break;
    }
    result.push(candidate);
    referenceDate = candidateDate;
  }

  return result;
}
